import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { NgxFileDropEntry } from "ngx-file-drop";
import { Observable, BehaviorSubject, of } from "rxjs";
import { SessionService } from "src/app/services/session.service";
import { environment } from "src/environments/environment";
import { Logger } from "./logger-service";

@Injectable({
  providedIn: 'root'
})
export class FileManagementService {

  constructor(private http: HttpClient,
    private logger: Logger,
    private sessionService: SessionService,
  ) { }


  uploadMapFiles$(files: File[]) {
    const sessionId = this.sessionService.getSessionIdOrError();
    const url = `${environment.apiUrl}/session/upload-area-map?sessionKey=${encodeURIComponent(sessionId)}`;

    // Content-Type=multipart/form-data is required on the server
    const formData = new FormData();
    for (const file of files) {

      // loop through all possible files being uploaded
      formData.append("files[]", file);
    }

    return this.http.post(url, formData);
  }

  removeMapFiles$(mapAttachmentsToKeep: string[]) {

    const sessionId = this.sessionService.getSessionIdOrError();

    const modelForPost = {
      sessionKey: sessionId,
      mapAttachmentsToKeep: mapAttachmentsToKeep
    };

    const url = `${environment.apiUrl}/session/remove-map-files`;

    return this.http.post(url, modelForPost);

  }

  removeConsentFiles$(evidenceOfConsentToKeep: string[]) {

    const sessionId = this.sessionService.getSessionIdOrError();
    const url = `${environment.apiUrl}/session/remove-consent-files`;
    const modelForPost = {
      sessionKey: sessionId,
      evidenceOfConsentToKeep: evidenceOfConsentToKeep,
    };

    return this.http.post<string[]>(url, modelForPost);
  }

  uploadEvidenceOfConsent$(files: File[]) {
    const sessionId = this.sessionService.getSessionIdOrError();

    const url = `${environment.apiUrl}/session/upload-evidence-of-consent?sessionKey=${encodeURIComponent(sessionId)}`;
    // Content-Type=multipart/form-data is required on the server
    const formData = new FormData();
    for (const file of files) {

      // loop through all possible files being uploaded
      formData.append("files[]", file);
    }

    return this.http.post(url, formData);
  }

  convertNgxFileDropEntriesToFileSystems$(files: NgxFileDropEntry[]): Observable<File[]> {

    const fileSystems$ = new BehaviorSubject<File[]>([]);
    if (!files) return of([]);

    for (const droppedFile of files) {
      const fileEntry = droppedFile.fileEntry as FileSystemFileEntry;

      fileEntry.file((file: File) => {
        console.log(droppedFile.relativePath, file);
        fileSystems$.next([...fileSystems$.value, file])
      })

    }

    return fileSystems$
  }

  validEvidenceOfConsentMimeType = [
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/pdf',
    'application/vnd.oasis.opendocument.text',
    'image/jpeg',
    'image/bmp',
    'image/tiff',
    'image/png',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  ];

  validateEvidenceOfConsentMimeType(file: File) {
    return this.validEvidenceOfConsentMimeType.indexOf(file.type) != -1;
  }

  validMapMimeType = [
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/pdf',
    'application/vnd.oasis.opendocument.text',
    'image/jpeg',
    'image/bmp',
    'image/tiff',
    'image/png',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/x-rar-compressed',
    'application/zip',
    'application/x-7z-compressed',
    'application/dxf',
    'application/vnd.google-earth.kml+xml',
    'application/x-zip-compressed'
  ];

  validateMapMimeType(file: File) {
    return this.validMapMimeType.indexOf(file.type) != -1;
  }

  validExchangeAreaMimeType = [
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/pdf',
    'application/vnd.oasis.opendocument.text',
    'image/jpeg',
    'image/bmp',
    'image/tiff',
    'image/png',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/x-rar-compressed',
    'application/zip',
    'application/x-7z-compressed',
    'application/dxf',
    'application/vnd.google-earth.kml+xml',
    'application/x-zip-compressed'
  ];

  validateExchangeAreaMimeType(file: File) {
    return this.validExchangeAreaMimeType.indexOf(file.type) != -1;
  }

  validateFileEndingTypes(file: File) {
    return !file.name.toLowerCase().endsWith('.img')
      && !file.name.toLowerCase().endsWith('.rar')
      && !file.name.toLowerCase().endsWith('.zip')
      && !file.name.toLowerCase().endsWith('.kml')
      && !file.name.toLowerCase().endsWith('.kmz')
      && !file.name.toLowerCase().endsWith('.dxf')
      ||
      file.name.toLowerCase().endsWith('.exe');
  }


  fileHasValidCharacters(file: File) {
    const rg1 = /^[^\\/:\*\?"<>\|]+$/; // forbidden characters \ / : * ? " < > |
    const rg2 = /^\./; // cannot start with dot (.)
    const rg3 = /^(nul|prn|con|lpt[0-9]|com[0-9])(\.|$)/i; // forbidden file names

    return rg1.test(file.name) && !rg2.test(file.name) && !rg3.test(file.name);
  }
}
